/**
 * Task card component for displaying task status and basic information
 */

import { formatDistanceToNow } from 'date-fns'
import { 
  Play, 
  Pause, 
  Square, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ExternalLink,
  Eye
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type { Task, TaskStatus } from '../../types/anchor-browser'

interface TaskCardProps {
  task: Task
  onView?: (taskId: string) => void
  onStop?: (taskId: string) => void
  onPause?: (taskId: string) => void
  onResume?: (taskId: string) => void
  isLoading?: boolean
}

function getStatusIcon(status: TaskStatus) {
  switch (status) {
    case 'created':
      return <Clock className="h-4 w-4" />
    case 'running':
      return <Play className="h-4 w-4" />
    case 'paused':
      return <Pause className="h-4 w-4" />
    case 'finished':
      return <CheckCircle className="h-4 w-4" />
    case 'failed':
      return <XCircle className="h-4 w-4" />
    case 'stopped':
      return <Square className="h-4 w-4" />
    default:
      return <AlertCircle className="h-4 w-4" />
  }
}

function getStatusVariant(status: TaskStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case 'running':
      return 'default'
    case 'finished':
      return 'secondary'
    case 'failed':
      return 'destructive'
    case 'paused':
    case 'stopped':
      return 'outline'
    default:
      return 'secondary'
  }
}

function getStatusColor(status: TaskStatus) {
  switch (status) {
    case 'created':
      return 'text-blue-600'
    case 'running':
      return 'text-green-600'
    case 'paused':
      return 'text-yellow-600'
    case 'finished':
      return 'text-green-600'
    case 'failed':
      return 'text-red-600'
    case 'stopped':
      return 'text-gray-600'
    default:
      return 'text-gray-600'
  }
}

function calculateProgress(task: Task): number {
  if (task.status === 'finished') return 100
  if (task.status === 'failed' || task.status === 'stopped') return 0
  if (!task.steps || task.steps.length === 0) return 0

  // Estimate progress based on steps completed vs typical task length
  const stepsCompleted = task.steps.length
  const estimatedTotalSteps = 50 // Rough estimate
  return Math.min((stepsCompleted / estimatedTotalSteps) * 100, 95)
}

export function TaskCard({ 
  task, 
  onView, 
  onStop, 
  onPause, 
  onResume, 
  isLoading = false 
}: TaskCardProps) {
  const progress = calculateProgress(task)
  const canStop = task.status === 'running' || task.status === 'paused'
  const canPause = task.status === 'running'
  const canResume = task.status === 'paused'

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg truncate">{task.anchor_session_id || task.id}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <span>Created {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}</span>
              {task.finished_at && (
                <span>• Finished {formatDistanceToNow(new Date(task.finished_at), { addSuffix: true })}</span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Badge variant={getStatusVariant(task.status)} className="flex items-center gap-1">
              <span className={getStatusColor(task.status)}>
                {getStatusIcon(task.status)}
              </span>
              {task.status}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        {(task.status === 'running' || task.status === 'paused') && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            {task.steps && task.steps.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {task.steps.length} steps completed
              </p>
            )}
          </div>
        )}

        {/* Task Output */}
        {task.output && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Output</h4>
            <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
              {task.output.length > 200 ? `${task.output.substring(0, 200)}...` : task.output}
            </p>
          </div>
        )}

        {/* Current Step */}
        {task.status === 'running' && task.steps && task.steps.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Current Step</h4>
            <p className="text-sm text-muted-foreground">
              {task.steps[task.steps.length - 1]?.next_goal || 'Processing...'}
            </p>
          </div>
        )}

        {/* Live URL */}
        {task.live_url && task.status === 'running' && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md">
            <Eye className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-600 font-medium">Live session available</span>
          </div>
        )}

        {/* Public Share URL */}
        {task.public_share_url && (
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <a 
              href={task.public_share_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              View public share
            </a>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView?.(task.id)}
            className="flex-1"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>

          {canResume && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onResume?.(task.id)}
              disabled={isLoading}
            >
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          )}

          {canPause && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPause?.(task.id)}
              disabled={isLoading}
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
          )}

          {canStop && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onStop?.(task.id)}
              disabled={isLoading}
            >
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
